import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { Fragment } from 'react';
import { useTranslate } from 'react-admin';
import { match } from 'ts-pattern';

import { EmployeeExcelColumnMapping } from '@Common/types/bo/b2b/employees/bulk_create';

import { isRequiredFieldError } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/utils/isRequiredFieldError';
import { translateBackendError } from '@sh4res/bo-common/utils/translate_error';

interface DetailsRowProps {
  action: 'error' | 'update';
  row: Record<string, string | null>;
  rowNumber: number;
  info: Record<string, string | number | null> | null;
  infoIgnored?: Record<string, string | number | null> | null;
  highlightRowClassName: string;
  defaultRowClassName?: string;
  highlightCellClassName: string;
  // Defaults to EmployeeExcelColumnMapping; the multi-company screens pass
  // EmployeeMultiCompanyExcelColumnMapping instead, to also render the Company ID column.
  columnMapping?: Record<string, string>;
}

function getFieldDisplay({
  action,
  key,
  fieldName,
  fieldValue,
  fileValue,
  hasError,
  isEmpty,
  isIgnored,
  translate,
}: {
  action: 'error' | 'update';
  key: string;
  fieldName: string;
  fieldValue?: string | number | null;
  fileValue: string | null;
  hasError: boolean;
  isEmpty: boolean;
  isIgnored: boolean;
  translate: ReturnType<typeof useTranslate>;
}) {
  if (isIgnored) {
    return fieldValue;
  }

  if (!isEmpty) {
    return fileValue;
  }

  if (key === 'csp_code') {
    return translate(
      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.csp_code.no_code',
    );
  }

  if (G.isNotNullable(fieldValue) && action === 'update') {
    return translate(
      'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.field_not_provided',
      { field: fieldName },
    );
  }

  if (hasError) {
    return translate(
      'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.field_incorrect',
      { field: fieldName },
    );
  }

  return fileValue;
}

export function DetailsRow(props: DetailsRowProps) {
  const translate = useTranslate();
  const columnMapping = props.columnMapping ?? EmployeeExcelColumnMapping;

  const getColumnErrorTitle = (
    key: string,
    value: string,
    error: string | number | null | undefined,
    isIgnored: boolean,
  ) => {
    if (isIgnored) {
      return translate(
        key === 'email'
          ? 'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.ignored_email'
          : 'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.ignored',
      );
    }

    if (error === undefined) {
      return undefined;
    }

    if (error === null) {
      return translate(
        key === 'csp_code'
          ? 'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.csp_code.no_code'
          : 'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.field_not_provided',
        { field: value },
      );
    }

    const errorString = error.toString();

    if (isRequiredFieldError(errorString)) {
      return translate(
        'resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.required',
      );
    } else if (errorString.match(/^format_invalid$/)) {
      return translate('resources.bo_common.b2b.employees.validation.nir.format_invalid');
    }

    const translatedErrors = extractErrorKeys(errorString).map((error) =>
      translateBackendError(
        {
          resource: getResourceKey(key),
          error,
        },
        (translation) => translate(translation),
      ),
    );

    return translatedErrors.length > 1
      ? translatedErrors.map((translatedError) => `- ${translatedError}`).join('\n')
      : translatedErrors[0];
  };

  return (
    <Box
      display="contents"
      className={props.info ? props.highlightRowClassName : props.defaultRowClassName}>
      <Box padding={1} textAlign="right">
        {props.rowNumber}
      </Box>
      <Box padding={1} display="flex" flexWrap="wrap">
        {Object.entries(columnMapping).map(([key, value], idx) => {
          const hasError = !G.isUndefined(props.info?.[key]);
          const isEmpty = props.row[key] === '' || G.isNullable(props.row[key]);
          const isIgnored = G.isNotNullable(props.infoIgnored?.[key] ?? null);
          const fieldDisplay = getFieldDisplay({
            action: props.action,
            key,
            fieldName: value,
            fieldValue: props.info?.[key] ?? props.infoIgnored?.[key],
            fileValue: props.row[key],
            hasError,
            isEmpty,
            isIgnored,
            translate,
          });

          return (
            <Fragment key={key}>
              <span
                className={hasError ? props.highlightCellClassName : undefined}
                style={{ color: isIgnored ? '#F09144' : '' }}
                title={getColumnErrorTitle(key, value, props.info?.[key], isIgnored)}>
                {fieldDisplay}
              </span>
              {idx < Object.keys(columnMapping).length - 1 ? ',' : null}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

const extractErrorKeys = (input: string): string[] => {
  // Match keys in the format (first_key) / (first_key),(second_key),(third_key)
  const VALID = /^\([a-z_]+\)(?:,\([a-z_]+\))*$/;
  if (!VALID.test(input)) {
    return [input];
  }
  return input.split(',');
};

const getResourceKey = (key: string) =>
  match(key)
    .with('nir', () => 'resources.bo_common.b2b.employees.validation.nir')
    .otherwise(
      () =>
        `resources.bo_common.b2b.employees.actions.bulk_create.sections.validation_errors.fields.${key}`,
    );
